import moment, { type Moment } from 'moment-timezone';
import { formatTimezoneAbbreviationForDisplay } from '@/utils/formatTimezoneAbbreviation';

/** Moment format strings aligned with `useDateFormat` (profile date_fmt / time_fmt). */
export type CustomTimingDropdownFormatOptions = {
  /** Date segment only, e.g. `dateFormat.label` from `useDateFormat` */
  dateMomentFormat: string;
  /** Time segment: `hh:mm A` (12h) or `HH:mm` (24h) from `useDateFormat` */
  timeMomentFormat: string;
};

/**
 * Fragment shown after "Custom date > " in the timing KSelect (wall time in candidate TZ).
 * Example: 06/01/2025 at 07:30 AM (PST) — date/time shapes follow the current user's profile.
 */
export function formatCustomTimingInstantForDropdown(
  m: Moment,
  ianaTimezoneKey: string,
  { dateMomentFormat, timeMomentFormat }: CustomTimingDropdownFormatOptions
): string {
  const zoneLabel = formatTimezoneAbbreviationForDisplay(m, ianaTimezoneKey);
  return `${m.format(dateMomentFormat)} at ${m.format(timeMomentFormat)} (${zoneLabel})`;
}

/**
 * IANA zone used for preset slots (today 6pm / tomorrow …) and for timing-option labels.
 * Candidate zone when valid; otherwise the recruiter's zone.
 */
export function getPresetScheduleAnchorTimezoneKey(
  hasCandidateTimezone,
  candidateTimezoneKey,
  userTimezoneKey
) {
  return hasCandidateTimezone ? candidateTimezoneKey : userTimezoneKey;
}

/**
 * KSelect options: wall times are interpreted in `displayTimezoneKey` (candidate or recruiter fallback).
 * Omits "Today at 6:00 PM" when 6:00 PM today in that zone is not strictly in the future.
 * Zone suffix uses shared timezone-abbreviation formatting utility.
 */
export function getScheduleTimingSelectOptions({
  hasCandidateTimezone,
  displayTimezoneKey,
}) {
  const todaySixPmInZone = moment()
    .tz(displayTimezoneKey)
    .startOf('day')
    .add(18, 'hours');
  const showTodayEvening = todaySixPmInZone.isAfter(moment());

  const anchor = moment().tz(displayTimezoneKey);
  const tomorrowMorning = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(9, 'hours');
  const tomorrowAfternoon = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(14, 'hours');
  const tomorrowEvening = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(18, 'hours');

  const options = [{ value: 'immediately', label: 'Now (Immediately)' }];
  if (showTodayEvening) {
    options.push({
      value: 'today_evening',
      label: `Today 6:00 PM ${formatTimezoneAbbreviationForDisplay(todaySixPmInZone, displayTimezoneKey)}`,
    });
  }
  options.push(
    {
      value: 'tomorrow_morning',
      label: `Tomorrow 9:00 AM ${formatTimezoneAbbreviationForDisplay(tomorrowMorning, displayTimezoneKey)}`,
    },
    {
      value: 'tomorrow_afternoon',
      label: `Tomorrow 2:00 PM ${formatTimezoneAbbreviationForDisplay(tomorrowAfternoon, displayTimezoneKey)}`,
    },
    {
      value: 'tomorrow_evening',
      label: `Tomorrow 6:00 PM ${formatTimezoneAbbreviationForDisplay(tomorrowEvening, displayTimezoneKey)}`,
    }
  );
  if (hasCandidateTimezone) {
    options.push({ value: 'custom', label: 'Custom date' });
  }
  return options;
}

/** True when contact has a non-empty IANA timezone known to moment-timezone. */
export function hasValidCandidateTimezone(contact) {
  const tz = contact?.time_zone;
  return Boolean(tz && typeof tz === 'string' && moment.tz.zone(tz));
}

/** Parses { date, time, timezoneKey } into a moment in that zone, or null if incomplete/invalid. */
export function parseCustomScheduleInstant(customSchedule) {
  if (
    !customSchedule?.date ||
    !customSchedule?.time ||
    !customSchedule?.timezoneKey
  ) {
    return null;
  }
  const m = moment.tz(
    `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
    customSchedule.timezoneKey
  );
  return m.isValid() ? m : null;
}

/** Whether the chosen local wall time is strictly after now (UTC), with a small buffer. */
export function isCustomScheduleInFuture(customSchedule, bufferMinutes = 1) {
  const m = parseCustomScheduleInstant(customSchedule);
  if (!m) return false;
  return m.isAfter(moment().subtract(bufferMinutes, 'minutes'));
}
